import { Parser } from '@json2csv/plainjs';

export const baseCsvFields = [
  {
    label: 'Node Number',
    value: 'nodeNumber',
  },
  {
    label: 'Node Type',
    value: (record?: { nodeType?: string }) => {
      if (record?.nodeType) {
        return record.nodeType.toUpperCase();
      }

      return 'D';
    },
  },
  {
    label: 'Node Name',
    value: 'nodeName',
  },
  {
    label: 'Intent',
    value: 'intent',
  },
  {
    label: 'NLU Disabled?',
    value: (record?: { nluDisabled?: boolean }) => {
      if (record?.nluDisabled === true) return 1;
      return undefined;
    },
  },
  {
    label: 'Next Nodes',
    value: (record?: { nextNodes?: number[] }) => {
      // PSTUDIO-4081: `Array.isArray`, like every other array-valued field
      // below. Without it a row holding the RAW typed text of an open panel
      // edit (PSTUDIO-3882 writes that draft per keystroke) passed the
      // `.length` gate on its CHARACTER count and published `nextNodes[0]` —
      // the character "1" for "12,13". Every CSV built from the live model is
      // affected: the Bot Compiler's bot.csv, the published bot.csv, and the
      // grouped CSV in the download bundle.
      if (Array.isArray(record?.nextNodes) && record.nextNodes.length) {
        return record.nextNodes[0];
      }

      return '';
    },
  },
  {
    label: 'Message',
    value: 'message',
  },
  {
    label: 'Rich Asset Type',
    value: (record?: { richAssetType?: string }) => {
      if (!record?.richAssetType) return undefined;
      if (record.richAssetType === 'none') return undefined;

      return record.richAssetType;
    },
  },
  {
    label: 'Rich Asset Content',
    value: (record?: { richAssetContent?: string }) => {
      if (!record?.richAssetContent) return undefined;
      return record.richAssetContent;
    },
  },
  {
    label: 'Answer Required?',
    value: (record: { answerRequired?: boolean }) => {
      if (record.answerRequired === true) return 1;
      return undefined;
    },
  },
  {
    label: 'Behaviors',
    value: (record?: { behaviors?: string[] }) => {
      if (
        record?.behaviors &&
        Array.isArray(record.behaviors) &&
        record?.behaviors.length
      ) {
        return record.behaviors.join(',');
      }

      return null;
    },
  },
  {
    label: 'Command',
    value: 'command',
  },
  {
    label: 'Description',
    value: 'description',
  },
  {
    label: 'Output',
    value: (record?: { output?: string[] }) => {
      if (
        record?.output &&
        Array.isArray(record.output) &&
        record?.output.length
      ) {
        return record.output.join(',');
      }

      return null;
    },
  },
  {
    label: 'Node Input',
    value: 'nodeInput',
  },
  {
    label: 'Parameter Input',
    value: 'parameterInput',
  },
  {
    label: 'Decision Variable',
    value: 'decisionVariable',
  },
  {
    label: 'What Next?',
    value: (record?: { whatsNext?: string[] }) => {
      if (
        record?.whatsNext &&
        Array.isArray(record.whatsNext) &&
        record?.whatsNext.length
      ) {
        return record.whatsNext?.join('|');
      }
      return null;
    },
  },
  {
    label: 'Node Tags',
    value: 'nodeTags',
  },
  {
    label: 'Skill Tag',
    value: 'skillTag',
  },
  {
    label: 'Variable',
    value: (record?: { variables?: string[] }) => {
      if (
        record?.variables &&
        Array.isArray(record.variables) &&
        record?.variables.length
      ) {
        return record.variables.join(',');
      }

      return '';
    },
  },
  {
    label: 'Platform Flag',
    value: 'platformFlag',
  },
  {
    label: 'Utterances',
    value: 'utterances',
  },
  {
    label: 'CSS Classname',
    value: 'cssClassname',
  },
  {
    label: 'Event',
    value: 'analyticsEvent',
  },
];

export const json2csvParser = new Parser({ fields: baseCsvFields });

// The 4 grouping columns read the `__group*` props the record builder derives.
// The param uses an all-optional shape (not `Record<string, unknown>`) so the
// getter stays assignable to json2csv's `FieldInfo` value signature — `object`
// is assignable to an all-optional-props type but NOT to an index signature,
// which is what makes the spread `[...baseCsvFields, ...groupingCsvFields]`
// type-check the same way `baseCsvFields` alone does.
interface GroupedCsvFieldRecord {
  __groupId?: unknown;
  __groupName?: unknown;
  __groupOrder?: unknown;
  __groupCollapsed?: unknown;
  __groupColor?: unknown;
}

const asText = (v: unknown): string => (v == null ? '' : String(v));

export const groupingCsvFields = [
  {
    label: 'Group Id',
    value: (r?: GroupedCsvFieldRecord) => asText(r?.__groupId),
  },
  {
    label: 'Group Name',
    value: (r?: GroupedCsvFieldRecord) => asText(r?.__groupName),
  },
  {
    label: 'Group Order',
    value: (r?: GroupedCsvFieldRecord) => asText(r?.__groupOrder),
  },
  {
    label: 'Group Collapsed',
    value: (r?: GroupedCsvFieldRecord) => asText(r?.__groupCollapsed),
  },
  {
    label: 'Group Color',
    value: (r?: GroupedCsvFieldRecord) => asText(r?.__groupColor),
  },
];

// Event is the last base column, so appending == "after Event". If a future edit
// reorders base columns, splice after the 'Event'-labeled field instead.
export const json2csvParserWithGrouping = new Parser({
  fields: [...baseCsvFields, ...groupingCsvFields],
});
